import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Container, Box, Typography, Paper, Button, Grid, TextField, MenuItem, 
  Select, FormControl, InputLabel, Chip, OutlinedInput, Checkbox, ListItemText,
  Tabs, Tab, Divider, List, ListItem, ListItemIcon, Avatar, IconButton, InputBase,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PersonIcon from '@mui/icons-material/Person';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventIcon from '@mui/icons-material/Event';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import axios from 'axios';
import API_URL from '../config';
import Loading from '../components/Loading';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function TicketDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [types, setTypes] = useState([]);
  const [tabValue, setTabValue] = useState(0);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  const [assignUser, setAssignUser] = useState('');
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: 'Open',
    requested_by: '',
    sender_email: '',
    project_id: '',
    ticket_type_id: '',
    assigned_to_ids: [],
    priority: 'Medium',
    resolution: '',
    due_date: '',
    done_at: '',
    created_at: ''
  });

  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('user_id');
  const headers = { Authorization: `Bearer ${token}` };

  const getStatusColor = (status) => {
      switch(status) {
          case 'Done': return 'success';
          case 'In Progress': return 'info';
          case 'Open': return 'warning';
          default: return 'default';
      }
  };

  const getPriorityColor = (priority) => {
      switch(priority) {
          case 'Critical': return 'error';
          case 'High': return 'warning';
          case 'Medium': return 'info';
          case 'Low': return 'success';
          default: return 'default';
      }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [usersRes, projectsRes, typesRes] = await Promise.all([
          axios.get(`${API_URL}/team`, { headers }),
          axios.get(`${API_URL}/projects`, { headers }),
          axios.get(`${API_URL}/ticket-types`, { headers })
        ]);
        
        setUsers(usersRes.data);
        setProjects(projectsRes.data);
        setTypes(typesRes.data);

        if (!isNew) {
          const ticketRes = await axios.get(`${API_URL}/tickets/${id}`, { headers });
          const t = ticketRes.data;
          
          // Helper to convert UTC string to Local ISO string for datetime-local input
          const toLocal = (s) => {
             if (!s) return '';
             // Append Z if missing to treat as UTC
             const d = new Date(s.endsWith('Z') ? s : s + 'Z');
             const offset = d.getTimezoneOffset() * 60000;
             return new Date(d.getTime() - offset).toISOString().slice(0, 16);
          };

          setFormData({
            title: t.title,
            description: t.description || '',
            status: t.status,
            requested_by: t.requested_by || '',
            sender_email: t.sender_email || '',
            project_id: t.project_id || '',
            ticket_type_id: t.ticket_type_id || '',
            assigned_to_ids: t.assigned_to_ids || [],
            priority: t.priority || 'Medium',
            resolution: t.resolution || '',
            due_date: t.due_date || '',
            done_at: toLocal(t.done_at),
            created_at: toLocal(t.created_at)
          });
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleAssignUser = async () => {
    if (assignUser && !formData.assigned_to_ids.includes(assignUser)) {
      const newAssignments = [...formData.assigned_to_ids, assignUser];
      setFormData({
        ...formData,
        assigned_to_ids: newAssignments
      });

      if (!isNew) {
        try {
          await axios.put(`${API_URL}/tickets/${id}`, { assigned_to_ids: newAssignments }, { headers });
        } catch (err) {
          console.error("Error updating assignments:", err);
          alert("Failed to update assignments");
        }
      }
    }
    setOpenAssignDialog(false);
    setAssignUser('');
  };

  const handleRemoveUser = async (userId) => {
    const newAssignments = formData.assigned_to_ids.filter(id => id !== userId);
    setFormData({
      ...formData,
      assigned_to_ids: newAssignments
    });

    if (!isNew) {
      try {
        await axios.put(`${API_URL}/tickets/${id}`, { assigned_to_ids: newAssignments }, { headers });
      } catch (err) {
        console.error("Error updating assignments:", err);
        alert("Failed to update assignments");
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newFormData = { ...formData, [name]: value };

    if (name === 'status') {
        if (value === 'Done') {
            // Set done_at to current local datetime (YYYY-MM-DDTHH:mm)
            const now = new Date();
            now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
            newFormData.done_at = now.toISOString().slice(0, 16);
        } else {
            newFormData.done_at = '';
        }
    }
    setFormData(newFormData);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };



  const handleSave = async () => {
    try {
      const payload = { ...formData };
      
      // Convert local done_at back to UTC before sending
      if (payload.done_at) {
          const localDate = new Date(payload.done_at);
          payload.done_at = localDate.toISOString();
      }

      if (isNew) {
        payload.created_by_id = userId;
        await axios.post(`${API_URL}/tickets`, payload, { headers });
        navigate('/tickets');
      } else {
        await axios.put(`${API_URL}/tickets/${id}`, payload, { headers });
        // Keep in edit mode
      }
    } catch (err) {
      alert('Error saving ticket');
      console.error(err);
    }
  };

  if (loading) return <Loading />;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ p: 3, mb: 4, borderRadius: 3, bgcolor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate('/tickets')} sx={{ color: 'white', bgcolor: 'rgba(255,255,255,0.1)', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 0.5 }}>
                <InputBase
                    value={formData.title}
                    name="title"
                    onChange={handleChange}
                    placeholder="Ticket Title"
                    sx={{ 
                        color: 'inherit', 
                        fontSize: '2.125rem', 
                        fontWeight: 'bold',
                        '& .MuiInputBase-input': { p: 0 }
                    }}
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {!isNew && (
                    <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                    {`ACTS - T - ${String(id).padStart(5, '0')}`}
                    </Typography>
                )}
                <Chip 
                  label={formData.status} 
                  color={getStatusColor(formData.status)}
                  sx={{ fontWeight: 'bold', height: 24 }} 
                />
              </Box>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
           
            <Button 
              variant="contained" 
              sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
              onClick={handleSave}
              startIcon={<SaveIcon />}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabs Header */}
      <Paper sx={{ mb: 3, borderRadius: 3, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }} elevation={0}>
        <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', px: 2 }}
        >
            <Tab icon={<AssignmentIcon />} label="Ticket Details" iconPosition="start" sx={{ minHeight: 64 }} />
            <Tab icon={<PersonAddIcon />} label="Assign To" iconPosition="start" sx={{ minHeight: 64 }} />
            <Tab icon={<CheckCircleIcon />} label="Resolution" iconPosition="start" sx={{ minHeight: 64 }} />
        </Tabs>
      </Paper>

      {/* Tab 1: Ticket Details */}
      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
            {/* Left Column: Description */}
            <Grid item xs={12} md={8}>
                <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%', display: 'flex', flexDirection: 'column' }} elevation={0}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>Description</Typography>
                    <TextField 
                        fullWidth 
                        multiline 
                        minRows={12} 
                        name="description" 
                        value={formData.description} 
                        onChange={handleChange} 
                        placeholder="Enter ticket description..."
                        variant="outlined"
                        sx={{ 
                            flex: 1,
                            '& .MuiOutlinedInput-root': { 
                                height: '100%', 
                                alignItems: 'flex-start' 
                            } 
                        }}
                    />
                </Paper>
            </Grid>

            {/* Right Column: Meta Data */}
            <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Status & Type */}
                    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>Status & Type</Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Status</InputLabel>
                                    <Select
                                        name="status"
                                        value={formData.status}
                                        label="Status"
                                        onChange={handleChange}
                                        renderValue={(selected) => (
                                            <Chip label={selected} size="small" color={getStatusColor(selected)} />
                                        )}
                                    >
                                        <MenuItem value="Open">Open</MenuItem>
                                        <MenuItem value="In Progress">In Progress</MenuItem>
                                        <MenuItem value="Done">Done</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Priority</InputLabel>
                                    <Select
                                        name="priority"
                                        value={formData.priority}
                                        label="Priority"
                                        onChange={handleChange}
                                        renderValue={(selected) => (
                                            <Chip label={selected} size="small" color={getPriorityColor(selected)} />
                                        )}
                                    >
                                        <MenuItem value="Low">Low</MenuItem>
                                        <MenuItem value="Medium">Medium</MenuItem>
                                        <MenuItem value="High">High</MenuItem>
                                        <MenuItem value="Critical">Critical</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        name="ticket_type_id"
                                        value={formData.ticket_type_id}
                                        label="Type"
                                        onChange={handleChange}
                                    >
                                        {types.map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Context */}
                    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>Context</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Related Project</InputLabel>
                                <Select
                                    name="project_id"
                                    value={formData.project_id}
                                    label="Related Project"
                                    onChange={handleChange}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {projects.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <TextField 
                                fullWidth 
                                size="small"
                                label="Due Date" 
                                type="date"
                                name="due_date" 
                                value={formData.due_date} 
                                onChange={handleChange} 
                                InputLabelProps={{ shrink: true }}
                            />
                            {!isNew && (
                                <TextField 
                                    fullWidth 
                                    size="small"
                                    label="Created On" 
                                    type="datetime-local"
                                    value={formData.created_at} 
                                    InputProps={{ readOnly: true }}
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}
                        </Box>
                    </Paper>

                    {/* People */}
                    <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }} elevation={0}>
                        <Typography variant="h6" fontWeight="bold" gutterBottom>People</Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <TextField 
                                fullWidth 
                                size="small"
                                label="Requested By" 
                                name="requested_by" 
                                value={formData.requested_by} 
                                onChange={handleChange} 
                            />
                            <TextField 
                                fullWidth 
                                size="small"
                                label="Contact Email" 
                                name="sender_email" 
                                value={formData.sender_email} 
                                onChange={handleChange} 
                            />
                        </Box>
                    </Paper>
                </Box>
            </Grid>
        </Grid>
      </TabPanel>

      {/* Tab 2: Assign To */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" fontWeight="bold">Assigned Team Members</Typography>
            <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => setOpenAssignDialog(true)}>
                Add Member
            </Button>
        </Box>
        <Grid container spacing={2}>
            {formData.assigned_to_ids.map((id) => {
                const member = users.find(u => u.id === id);
                if (!member) return null;
                return (
                  <Grid item xs={12} sm={6} key={member.id}>
                    <Paper variant="outlined" sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.main', fontWeight: 'bold' }}>{member.name ? member.name[0] : '?'}</Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight="medium">{member.name}</Typography>
                          <Chip label={member.role || 'Member'} size="small" variant="outlined" sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }} />
                        </Box>
                      </Box>
                      <IconButton size="small" color="error" onClick={() => handleRemoveUser(member.id)} sx={{ bgcolor: 'rgba(211, 47, 47, 0.1)', '&:hover': { bgcolor: 'error.main', color: 'white' } }}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Paper>
                  </Grid>
                );
            })}
            {formData.assigned_to_ids.length === 0 && (
                <Grid item xs={12}>
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'divider' }}>
                    <Typography color="textSecondary">No team members assigned yet.</Typography>
                </Box>
                </Grid>
            )}
        </Grid>
      </TabPanel>

      {/* Tab 3: Resolution */}
      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', width: '100%' }} elevation={0}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>Resolution Details</Typography>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <TextField 
                        fullWidth multiline rows={8} 
                        label="Resolution Notes" 
                        name="resolution" 
                        value={formData.resolution} 
                        onChange={handleChange} 
                        placeholder="Describe how the ticket was resolved..."
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField 
                        fullWidth 
                        type="datetime-local"
                        label="Completed On" 
                        name="done_at" 
                        value={formData.done_at} 
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <TextField 
                        fullWidth 
                        label="Duration" 
                        value={(() => {
                            if (!formData.created_at || !formData.done_at) return '';
                            const start = new Date(formData.created_at);
                            const end = new Date(formData.done_at);
                            const diff = end - start;
                            if (diff < 0) return 'Invalid';
                            const days = Math.floor(diff / (1000 * 60 * 60 * 24));
                            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
                            return `${days}d ${hours}h`;
                        })()} 
                        InputProps={{ readOnly: true }}
                        InputLabelProps={{ shrink: true }}
                    />
                </Grid>
            </Grid>
        </Paper>
      </TabPanel>

      <Dialog open={openAssignDialog} onClose={() => setOpenAssignDialog(false)}>
        <DialogTitle>Assign User</DialogTitle>
        <DialogContent sx={{ minWidth: 300 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel>User</InputLabel>
            <Select value={assignUser} onChange={(e) => setAssignUser(e.target.value)} label="User">
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>{u.name} ({u.role || 'Member'})</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>Cancel</Button>
          <Button onClick={handleAssignUser} variant="contained">Assign</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default TicketDetails;
